import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { videoUrl } from "../../lib/api";

/**
 * ProductsHorizontalSnap — bandeau cinématique d'ambiance "Les essentiels du
 * snacking pro". Pas une grille de fiches produits, pas une logique SKU :
 * deux séquences premium "produit qui tombe" pour incarner l'univers snacking
 * — kebab, poulet croustillant — sous une seule promesse éditoriale.
 *
 * Le fichier garde son ancien nom pour ne pas casser l'import Home.jsx. Le
 * commit précédent (be23afc) avait dérivé en "fiches produits avec titres
 * SKU + cross-sell" — recadré ici en bandeau éditorial pur.
 *
 * Ce qu'on N'AFFICHE PAS :
 *   - titres produits ("Lamelles de kebab poulet", "Crispy strips tenders")
 *   - noms de SKU ou de marque
 *   - sous-link "Voir la gamme"
 *   - lien vers /produit/<slug>
 *
 * Ce qu'on AFFICHE :
 *   - eyebrow + h2 + tagline globaux (UN message pour le bandeau)
 *   - 2 vidéos en lecture muette, captions génériques optionnelles
 *     ("Kebab", "Poulet croustillant") — pas de nom de produit précis
 *   - 1 CTA "Voir le catalogue" → /contact?request=catalogue
 *
 * Performance vidéo identique au commit précédent : MP4 H.264 720×720,
 * 1.5–1.8 MB, lazy mount via IntersectionObserver, autoplay muted loop
 * playsInline, poster JPG, prefers-reduced-motion: reduce → paused.
 */

const VIDEOS = [
  {
    src: videoUrl("produit-kebab-poulet.mp4"),
    poster: videoUrl("produit-kebab-poulet-poster.jpg"),
    caption: "Kebab",
  },
  {
    src: videoUrl("produit-tenders-poulet.mp4"),
    poster: videoUrl("produit-tenders-poulet-poster.jpg"),
    caption: "Poulet croustillant",
  },
];

const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
};

/**
 * AmbientVideo — lazy-mounted, attaches src only when the section enters the
 * viewport. Pause + reset on reduced-motion, autoplay loop otherwise.
 */
const AmbientVideo = ({ src, poster, reducedMotion }) => {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!wrapperRef.current || ready) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setReady(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, [ready]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reducedMotion) {
      v.pause();
      v.currentTime = 0;
    } else if (ready) {
      v.play().catch(() => {});
    }
  }, [ready, reducedMotion]);

  return (
    <div ref={wrapperRef} className="absolute inset-0">
      <video
        ref={videoRef}
        src={ready ? src : undefined}
        poster={poster}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  );
};

export const ProductsHorizontalSnap = () => {
  const reducedMotion = useReducedMotion();

  return (
    <section
      data-testid="products-horizontal-snap"
      className="relative bg-fd-bg border-t border-fd-border py-20 lg:py-28"
    >
      <div className="px-6 md:px-10 lg:px-14 mb-10 lg:mb-14 max-w-[1400px] mx-auto">
        <div className="mono-label-gold mb-4 flex items-center gap-3">
          <span className="w-8 h-px bg-fd-gold"></span>
          <span>Les essentiels du snacking pro</span>
        </div>
        <h2 className="display-xl text-4xl sm:text-5xl lg:text-6xl text-fd-text max-w-4xl">
          Kebab, poulet croustillant, frites. <br className="hidden lg:block" />
          <span className="text-gradient-gold">Les bases qui font tourner une carte.</span>
        </h2>
        <p className="mt-5 font-body text-base md:text-lg text-fd-text/65 max-w-2xl">
          Des références pensées pour le débit, la régularité et la marge. Catalogue B2B sur demande commerciale.
        </p>
      </div>

      <div className="px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {VIDEOS.map((v, i) => (
            <motion.figure
              key={v.src}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden bg-fd-elevated aspect-square md:aspect-[4/3]"
              data-testid={`ambient-video-${i}`}
            >
              <AmbientVideo src={v.src} poster={v.poster} reducedMotion={reducedMotion} />
              {/* Bottom-left caption — sober, generic, no SKU/brand */}
              <figcaption className="absolute bottom-4 left-4 mono-label-gold bg-fd-bg/70 backdrop-blur-sm px-3 py-1.5">
                {v.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Single bottom CTA — lead-first, pas de PDF public */}
        <div className="mt-12 lg:mt-14 flex flex-col sm:flex-row gap-3 sm:justify-center">
          <Link
            to="/catalogue-feuilletable"
            data-cursor-hover
            data-testid="essentiels-cta-catalog"
            className="btn-pill btn-primary-v2"
          >
            Voir le catalogue <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/contact"
            data-cursor-hover
            data-testid="essentiels-cta-sales"
            className="inline-flex items-center justify-center gap-2 px-7 py-[0.95rem] rounded-full border border-white/25 text-fd-text font-display font-medium uppercase tracking-[0.02em] text-[0.85rem] whitespace-nowrap hover:border-white transition-colors"
          >
            Parler à un commercial
          </Link>
        </div>
      </div>
    </section>
  );
};
